import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

import { AppFooter } from "@/components/AppFooter";
import { LeftNav } from "./LeftNav";
import { RightRail } from "./RightRail";

/**
 * Three-column shell for the Q&A surfaces (`/questions`, `/tags`,
 * `/tags/:slug`, `<QuestionPage>`). Information-dense, hairline dividers,
 * sticky left nav. Collapses to single column on `< lg`.
 *
 * Non-qa pages (Landing, Dashboard, the collaborative-doc editor) use
 * their own chrome — `QAShell` is opt-in.
 */
export function QAShell({
  children,
  /** Optional right-rail override. Defaults to the standard rail with
   *  live activity + hot tags. Pass `null` to suppress entirely. */
  rightRail,
}: {
  children: ReactNode;
  rightRail?: ReactNode | null;
}) {
  const { state, signOut } = useAuth();
  const isAuthed = state.status === "authenticated";

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <TopBar isAuthed={isAuthed} signOut={signOut} />
      <div className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4">
        <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)_300px] gap-4 lg:gap-6 py-4 sm:py-6">
          <aside className="lg:sticky lg:top-4 lg:self-start">
            <LeftNav />
          </aside>
          <main className="min-w-0">{children}</main>
          {rightRail !== null && (
            <aside className="lg:sticky lg:top-4 lg:self-start order-last">
              {rightRail ?? <RightRail />}
            </aside>
          )}
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

function TopBar({
  isAuthed,
  signOut,
}: {
  isAuthed: boolean;
  signOut: () => Promise<void>;
}) {
  return (
    <header className="border-b border-zinc-200 dark:border-zinc-800 sticky top-0 z-10 bg-white/95 dark:bg-zinc-950/95 backdrop-blur">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 font-semibold tracking-tight text-base sm:text-lg"
        >
          <img
            src="/coauthor-mark.svg"
            alt=""
            className="w-6 h-6"
            aria-hidden="true"
          />
          Coauthor
        </Link>
        <div className="flex-1 max-w-md hidden sm:block">
          {/* Search slot — wired later when full-text search lands. For now
              a passive placeholder keeps the visual rhythm. */}
          <div className="relative">
            <input
              type="search"
              placeholder="Search questions…"
              className="w-full text-sm-dense bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
              disabled
              aria-label="Search questions (coming soon)"
            />
          </div>
        </div>
        <nav className="flex items-center gap-2 sm:gap-4 text-sm-dense text-zinc-600 dark:text-zinc-400">
          <Link
            to="/getting-started"
            className="hidden sm:inline hover:text-zinc-900 dark:hover:text-zinc-100 font-medium"
          >
            Getting started
          </Link>
          <Link
            to="/documents"
            className="rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-900"
          >
            Documents
          </Link>
          <Link
            to="/ask"
            className="rounded-md bg-accent text-white px-3 py-1.5 font-medium hover:opacity-90 hidden sm:inline-block"
          >
            Ask
          </Link>
          {isAuthed ? (
            <>
              <Link
                to="/dashboard"
                className="hover:text-zinc-900 dark:hover:text-zinc-100 hidden md:inline"
              >
                Dashboard
              </Link>
              <Link
                to="/tokens"
                className="hover:text-zinc-900 dark:hover:text-zinc-100 hidden md:inline"
              >
                Tokens
              </Link>
              <Link
                to="/billing"
                className="hover:text-zinc-900 dark:hover:text-zinc-100 hidden md:inline"
              >
                Billing
              </Link>
              <button
                type="button"
                onClick={() => void signOut()}
                className="hover:text-zinc-900 dark:hover:text-zinc-100"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-900"
            >
              Sign in
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
